"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LANGUAGE_LABELS } from "@/lib/constants";

interface Topic {
  id: string;
  name: string;
}

export function GuestFilters({
  topics,
  basePath = "/gaeste-finden",
}: {
  topics: Topic[];
  basePath?: string;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [q, setQ] = useState(searchParams.get("q") || "");
  const [selectedTopics, setSelectedTopics] = useState<string[]>(searchParams.getAll("topic"));
  const [sprache, setSprache] = useState(searchParams.get("sprache") || "");

  const applyFilters = useCallback((overrides?: { q?: string; topics?: string[]; sprache?: string }) => {
    const params = new URLSearchParams();
    const fQ = overrides?.q ?? q;
    const fTopics = overrides?.topics ?? selectedTopics;
    const fSprache = overrides?.sprache ?? sprache;

    if (fQ) params.set("q", fQ);
    fTopics.forEach((t) => params.append("topic", t));
    if (fSprache) params.set("sprache", fSprache);

    router.push(`${basePath}?${params.toString()}`);
  }, [basePath, q, router, selectedTopics, sprache]);

  useEffect(() => {
    const currentQuery = searchParams.get("q") || "";
    const nextQuery = q.trim();
    if (nextQuery === currentQuery) return;

    const timeoutId = window.setTimeout(() => applyFilters({ q: nextQuery }), 300);
    return () => window.clearTimeout(timeoutId);
  }, [applyFilters, q, searchParams]);

  function toggleTopic(id: string) {
    const next = selectedTopics.includes(id)
      ? selectedTopics.filter((t) => t !== id)
      : [...selectedTopics, id];
    setSelectedTopics(next);
    applyFilters({ topics: next });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row">
        <Input
          placeholder="Name oder Beschreibung suchen..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") applyFilters({ q: q.trim() }); }}
          aria-describedby="guest-search-hint"
          className="flex-1"
        />
        <p id="guest-search-hint" className="sr-only">
          Die Suche aktualisiert sich automatisch während der Eingabe.
        </p>
        <select
          value={sprache}
          onChange={(e) => { setSprache(e.target.value); applyFilters({ sprache: e.target.value }); }}
          className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
        >
          <option value="">Alle Sprachen</option>
          {Object.entries(LANGUAGE_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {topics.map((topic) => (
          <Badge
            key={topic.id}
            variant={selectedTopics.includes(topic.id) ? "default" : "outline"}
            className="cursor-pointer select-none text-xs"
            onClick={() => toggleTopic(topic.id)}
          >
            {topic.name}
          </Badge>
        ))}
      </div>
    </div>
  );
}
